/**
 * Offline Patient Information Page
 *
 * FIXED: Offline patient updates were failing due to:
 * 1. Complex update logic that didn't preserve patient ID correctly
 * 2. Null value handling issues with IndexedDB
 * 3. Missing sync queue integration
 *
 * SOLUTION:
 * 1. Simplified update logic using patientOfflineStore.updatePatient()
 * 2. Proper null-to-undefined conversion for IndexedDB compatibility
 * 3. Integrated sync queuing for offline changes
 * 4. Added comprehensive logging for debugging
 */

import React, { useEffect, useState } from 'react'
import { useLocation, useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { usePatientIdentifierLabel } from '@/hooks/usePatientIdentifierLabel'
import { useSnackbar } from 'notistack'

import { Bars } from 'react-loader-spinner'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TabPanel from '@/components/ui/tab/TabPanel'
import Typography from '@mui/material/Typography'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloudOffIcon from '@mui/icons-material/CloudOff'
import SyncIcon from '@mui/icons-material/Sync'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

import colors from '@/assets/scss/_themes-vars.module.scss'
import ErrorTemplate from '@/components/ui/template/ErrorTemplate/ErrorTemplate'
import PageContainer from '@/components/ui/containers/PageContainer'
import Tabs from '@/components/ui/tabs/Tabs'
import { OfflineStatusIndicator } from '@/shared/offline'
import { patientOfflineStore, safelyConvertDateToString } from '@/core/offline'
import { OfflinePatient } from '@/core/offline/types/database.types'
import { useOfflineMode } from '@/providers/OfflineModeProvider'
import { UpdatePatientDto } from '@/core/models/patient.model'

import { OfflineDocuments } from './OfflineDocuments'
import { OfflineActions } from './OfflineActions'
import { PatientInfo } from '@/pages/patient/ui/PatientInfo'

export type OfflinePatientProps = {
  patientId?: string
  showInformationTab?: boolean
  showDocumentsTab?: boolean
}

export function OfflineInfoPatientPage(props: OfflinePatientProps) {
  const location = useLocation()
  const { patientId: paramPatientId } = useParams()
  const patientId = props.patientId || paramPatientId
  const { t } = useTranslation()
  const identifierLabel = usePatientIdentifierLabel()
  const { enqueueSnackbar } = useSnackbar()
  const { isOfflineDatabaseReady } = useOfflineMode()
  const isDialog = props.patientId ? true : false
  const { showInformationTab = true, showDocumentsTab = true } = props

  const [patient, setPatient] = useState<OfflinePatient | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [updating, setUpdating] = useState(false)

  useEffect(() => {
    if (isOfflineDatabaseReady && patientId) {
      loadPatient()
    }
  }, [patientId, isOfflineDatabaseReady])

  const loadPatient = async () => {
    if (!patientId) return

    try {
      setLoading(true)
      const loadedPatient = await patientOfflineStore.getByOfflineId(patientId)
      setPatient(loadedPatient || null)
    } catch (err) {
      console.error('Error loading offline patient:', err)
      setError(err as Error)
    } finally {
      setLoading(false)
    }
  }

  // Handle offline patient update - Using the dedicated patient store method
  const handleOfflinePatientUpdate = async (updatedData: UpdatePatientDto) => {
    if (!patient) {
      console.error('No patient to update')
      return
    }

    try {
      setUpdating(true)
      console.log('🔄 Updating offline patient:', {
        patientId: patient._id,
        offlineId: patient._offlineId,
        currentSyncStatus: patient._syncStatus,
      })

      // Prepare the update payload - convert null to undefined for IndexedDB compatibility
      const updatePayload: Partial<OfflinePatient> = {}

      // Update user info if provided
      if (updatedData.userInfo) {
        updatePayload.userInfo = {
          ...patient.userInfo,
          ...Object.fromEntries(Object.entries(updatedData.userInfo).map(([key, value]) => [key, value === null ? undefined : value])),
        }

        // Handle birthday conversion - it might be an ISO string from the form
        if (updatedData.userInfo.birthday !== undefined) {
          if (updatedData.userInfo.birthday === null) {
            updatePayload.userInfo!.birthday = undefined
          } else {
            // If it's already an ISO string, keep it as-is
            // If it's a Date object, convert it
            if (typeof updatedData.userInfo.birthday === 'string') {
              updatePayload.userInfo!.birthday = updatedData.userInfo.birthday
            } else {
              updatePayload.userInfo!.birthday = safelyConvertDateToString(updatedData.userInfo.birthday) || updatedData.userInfo.birthday
            }
          }
        }
      }

      // Update contact info if provided
      if (updatedData.contactInfo) {
        updatePayload.contactInfo = {
          ...patient.contactInfo,
          ...Object.fromEntries(Object.entries(updatedData.contactInfo).map(([key, value]) => [key, value === null ? undefined : value])),
        }
      }

      // Update other fields - only if explicitly provided (not undefined)
      // Convert null values to undefined for IndexedDB compatibility
      const fieldsToUpdate = ['email', 'weight', 'height', 'allergy', 'vaccins', 'notes', 'background', 'treatment', 'caisse', 'codeRegime', 'codeGestion']

      fieldsToUpdate.forEach((field) => {
        if (updatedData[field as keyof UpdatePatientDto] !== undefined) {
          const value = updatedData[field as keyof UpdatePatientDto]
          // Convert null to undefined, keep other values as-is
          updatePayload[field as keyof OfflinePatient] = (value === null ? undefined : value) as any
        }
      })

      // Handle complex objects that might have null values
      if (updatedData.attendingPhysician !== undefined) {
        if (updatedData.attendingPhysician === null) {
          updatePayload.attendingPhysician = undefined
        } else {
          updatePayload.attendingPhysician = Object.fromEntries(
            Object.entries(updatedData.attendingPhysician).map(([key, value]) => [key, value === null ? undefined : value])
          ) as any
        }
      }

      if (updatedData.parents !== undefined) {
        if (updatedData.parents === null) {
          updatePayload.parents = undefined
        } else {
          updatePayload.parents = updatedData.parents.map((parent) =>
            Object.fromEntries(Object.entries(parent).map(([key, value]) => [key, value === null ? undefined : value]))
          ) as any
        }
      }

      console.log('📝 Update payload prepared:', updatePayload)

      // Use the dedicated updatePatient method from the store
      const updatedPatient = await patientOfflineStore.updatePatient(patient._id, updatePayload)
      console.log('✅ Patient updated successfully:', {
        _id: updatedPatient._id,
        _syncStatus: updatedPatient._syncStatus,
        _lastModified: updatedPatient._lastModified,
      })

      // Reload the patient data to reflect changes
      await loadPatient()
      console.log('✅ Patient data reloaded successfully')

      enqueueSnackbar(t('notification.patient.update.success'), { variant: 'success' })
    } catch (err) {
      console.error('❌ Error updating offline patient:', err)
      enqueueSnackbar(t('notification.patient.update.error'), { variant: 'error' })
    } finally {
      setUpdating(false)
    }
  }

  const tabs = [
    showInformationTab && {
      label: t('page.infoPatient.informations.label'),
      component: patientId && (
        <PatientInfo patientId={patientId} patient={patient || undefined} isOffline={true} onUpdate={handleOfflinePatientUpdate} isUpdating={updating} />
      ),
    },
    showDocumentsTab &&
      patientId && {
        label: t('page.infoPatient.documents.label'),
        component: <OfflineDocuments patientId={patientId} />,
      },
  ].filter(Boolean) as Array<{ label: string; component: React.ReactElement }>

  // Show loading state while database is initializing
  if (!isOfflineDatabaseReady) {
    return (
      <PageContainer noPadding={isDialog}>
        <OfflineStatusIndicator />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Typography variant="body1" color="text.secondary">
            {t('offline.database.initializing')}
          </Typography>
        </Box>
      </PageContainer>
    )
  }

  if (!patient && loading) {
    return (
      <Stack height="100%" alignItems="center" justifyContent="center">
        <Bars height="80" width="80" color={colors.primaryMain} />
      </Stack>
    )
  }

  if (!patient && !loading && error) {
    return <ErrorTemplate code="NotFoundError" message={t('page.infoPatient.notFound')} />
  }

  return (
    <PageContainer noPadding={isDialog}>
      <OfflineStatusIndicator />
      <Stack direction="row" justifyContent="space-between" id="actions">
        {!isDialog && (
          <Button
            component={Link}
            size="small"
            to="/offline/patients"
            startIcon={<ArrowBackIcon />}
            state={{ from: { pathname: location.pathname, pattern: '/offline/patients/:patientId' } }}
            variant="neutral"
          >
            {t('common:button.back.label')}
          </Button>
        )}
        {!isDialog && patient && (
          <OfflineActions
            patient={patient}
            onPatientDeleted={() => {
              // Patient was deleted, we'll be navigated away
            }}
          />
        )}
      </Stack>
      <Paper sx={{ mt: '15px' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: '20px' }}>
          <Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" sx={{ fontSize: '18px', fontWeight: 'bold' }}>
                {patient?.userInfo.surname} {patient?.userInfo.name}
              </Typography>
              {patient && (
                <Chip
                  icon={patient._syncStatus === 'synced' ? <CheckCircleIcon /> : patient._syncStatus === 'pending' ? <SyncIcon /> : <CloudOffIcon />}
                  label={
                    patient._syncStatus === 'synced'
                      ? t('offline.patient.syncStatus.synced')
                      : patient._syncStatus === 'pending'
                      ? t('offline.patient.syncStatus.pending')
                      : t('offline.patient.syncStatus.offline')
                  }
                  size="small"
                  color={patient._syncStatus === 'synced' ? 'success' : patient._syncStatus === 'pending' ? 'warning' : 'default'}
                  sx={{ fontSize: '0.7rem', height: '24px' }}
                />
              )}
            </Stack>
            <Typography color="primary" variant="subtitle2" sx={{ fontSize: '14px', fontWeight: 'bold' }}>
              {t('page.infoPatient.title')}
            </Typography>
          </Stack>
          <Stack>
            <Typography variant="subtitle1" sx={{ color: colors.gray500, fontSize: '14px' }}>
              {identifierLabel}
            </Typography>
            <Typography variant="body1" sx={{ color: colors.gray900, fontSize: '14px' }}>
              {patient?.secu}
            </Typography>
          </Stack>
        </Stack>
        <Divider />
        <Tabs
          renderTabPanel={({ value }) => (
            <Box sx={{ p: '20px 16px' }}>
              {tabs.map((tab, index) => (
                <TabPanel key={index} value={value} index={index}>
                  {tab.component}
                </TabPanel>
              ))}
            </Box>
          )}
          tabs={tabs.map((tab) => ({ label: tab.label }))}
          variant="fullWidth"
        />
      </Paper>
    </PageContainer>
  )
}

export default OfflineInfoPatientPage
